import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useAuth } from '@matix/auth';
import {
  gatewayPaymentAPI,
  getApiErrorMessage,
  type TapTerminalMode,
} from '@matix/api-client';
import {
  PAYMENT_GATEWAYS,
  type CreateOrderRequest,
  type CreateOrderResponse,
  type PaymentGatewayCode,
  type PaymentType,
} from '@matix/types';
import { fmt } from '../../lib/mock-data';
import { printAfterCharge } from '../print';
import { useCart } from '../../store/cart';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { cashierIdentity } from '../../lib/cashierIdentity';

/**
 * Hosted-gateway payment dialog — Tap, UPayments, Tabby, Tamara, MyFatoorah.
 *
 * The parent (PaymentModal) builds a full single-payment CreateOrderRequest
 * with payments=[{ type_code: <the gateway's code>, amount: total }] and
 * hands it here. We open a charge (no GL voucher yet) and poll status every
 * 3s. On capture, the backend finalizes the order and returns the order
 * response; we print + signal success back to the parent.
 *
 * Two things differ from the Tap-only dialog this grew out of:
 *
 *   - A buy-now-pay-later provider confirms the customer with an SMS code, so
 *     it cannot open a session without a phone. When the cart's customer has
 *     none the dialog asks for it first, instead of opening the charge and
 *     receiving a 400 for it.
 *   - The basket lines travel with the charge, named, because those same
 *     providers price the basket line by line.
 *
 * Polling rather than webhooks per the integration decision — keeps the
 * cashier UI in control of the timing and avoids exposing a public webhook
 * endpoint. Manual "Refresh" button shortcuts the wait.
 */

const POLL_INTERVAL_MS = 3_000;

export interface GatewayPaymentDialogProps {
  gateway: PaymentGatewayCode;
  orderRequest: CreateOrderRequest;
  paymentTypes: PaymentType[];
  /** Called after order is captured + receipt printed. Modal closes upstream. */
  onSuccess: () => void;
  /** Called when user clicks "Cancel" or backend reports terminal failure. */
  onCancel: () => void;
}

export function GatewayPaymentDialog({
  gateway,
  orderRequest,
  paymentTypes,
  onSuccess,
  onCancel,
}: GatewayPaymentDialogProps): JSX.Element {
  const { cashier } = useAuth();
  const { t, lang } = useLang();
  const queryClient = useQueryClient();
  const cartLines = useCart((s) => s.lines);
  const customer = useCart((s) => s.customer);
  const table = useCart((s) => s.table);
  const reset = useCart((s) => s.reset);
  const info = PAYMENT_GATEWAYS[gateway];
  const gatewayName = lang === 'ar' ? info.nameAr : info.nameEn;

  type Phase = 'phone' | 'creating' | 'waiting' | 'succeeded' | 'failed' | 'cancelled';
  const customerPhone = (customer?.phone ?? '').trim();
  const needsPhone = info.requiresPhone && !customerPhone;
  const [phase, setPhase] = useState<Phase>(needsPhone ? 'phone' : 'creating');
  const [phoneInput, setPhoneInput] = useState('');
  const [chargeId, setChargeId] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [terminalMode, setTerminalMode] = useState<TapTerminalMode>('qr');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);

  // Refs to avoid stale-closure issues inside the polling interval.
  const chargeIdRef = useRef<string | null>(null);
  const phaseRef = useRef<Phase>(phase);
  /** True while a pollCharge request is awaiting a response — see pollOnce. */
  const pollInFlightRef = useRef(false);
  const orderResultRef = useRef<CreateOrderResponse | null>(null);

  const total = orderRequest.payments.reduce((s: number, p: { amount: number }) => s + p.amount, 0);

  function go(next: Phase): void {
    phaseRef.current = next;
    setPhase(next);
  }

  // ── 1. Open the charge once we are in 'creating' ────────────────────────
  useEffect(() => {
    if (phase !== 'creating') return;
    let cancelled = false;
    void (async () => {
      try {
        const phone = phoneInput.trim() || customerPhone;
        const resp = await gatewayPaymentAPI.createCharge(gateway, {
          order: orderRequest,
          ...(phone ? { customer: { phone_number: phone } } : {}),
          line_items: cartLines.map((ln) => ({
            name: (lang === 'ar' ? ln.product.nameAr : ln.product.name) || ln.product.name || `#${ln.product.id}`,
            qty: Math.abs(ln.qty),
            unit_price: ln.unitPrice,
            sku: String(ln.product.id),
          })),
        });
        if (cancelled) return;
        chargeIdRef.current = resp.charge_id;
        setChargeId(resp.charge_id);
        setRedirectUrl(resp.redirect_url);
        setTerminalMode(resp.terminal_mode);

        // Render the QR right away so the customer can scan while we
        // start the polling loop.
        if (resp.terminal_mode === 'qr' && resp.redirect_url) {
          try {
            const dataUrl = await QRCode.toDataURL(resp.redirect_url, {
              width: 320,
              margin: 1,
              errorCorrectionLevel: 'M',
            });
            if (!cancelled) setQrDataUrl(dataUrl);
          } catch (qrErr) {
            console.warn('Failed to render payment QR', qrErr);
          }
        }
        go('waiting');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(
          getApiErrorMessage(err) ||
            t({ en: `Failed to open ${gatewayName} payment`, ar: `فشل فتح عملية دفع ${gatewayName}` }),
        );
        go('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase === 'creating']);

  // ── 2. Poll every 3s while in 'waiting' ─────────────────────────────────
  useEffect(() => {
    if (phase !== 'waiting') return;
    const id = window.setInterval(() => {
      void pollOnce();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  async function pollOnce(): Promise<void> {
    const id = chargeIdRef.current;
    if (!id) return;
    if (phaseRef.current !== 'waiting') return;
    // setInterval fires on a fixed schedule whether or not the previous poll
    // has returned, and the poll that observes the capture also writes the
    // order — a slow one (seconds, not milliseconds) would otherwise be
    // overlapped by the next tick and the same paid charge finalized twice.
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    setPollCount((n) => n + 1);
    try {
      const resp = await gatewayPaymentAPI.pollCharge(id);
      if (resp.status === 'captured' && resp.order) {
        orderResultRef.current = resp.order;
        go('succeeded');
        // Print receipt (fire-and-forget) then signal success upstream.
        if (cashier) {
          try {
            printAfterCharge({
              queryClient,
              response: resp.order,
              request: orderRequest,
              cartLines,
              customer,
              table,
              tenantDb: cashier.tenant_db ?? null,
              cashier: cashierIdentity(cashier),
              paymentTypes,
            });
          } catch (printErr) {
            console.warn('Gateway print-after-charge failed', printErr);
          }
        }
        toast(
          t({
            en: `Order #${resp.order.entry_no} captured via ${gatewayName} · ${fmt(resp.order.totals.net_value)}`,
            ar: `تم تحصيل الطلب #${resp.order.entry_no} عبر ${gatewayName} · ${fmt(resp.order.totals.net_value)}`,
          }),
          'success',
        );
        reset();
        // Short delay so the cashier sees the success state before the modal closes.
        window.setTimeout(() => onSuccess(), 800);
      } else if (resp.status === 'failed' || resp.status === 'cancelled') {
        setErrorMessage(
          resp.failure_reason ||
            t({ en: `${gatewayName} charge ${resp.status}`, ar: `عملية ${gatewayName} ${resp.status}` }),
        );
        go('failed');
      }
      // 'pending' → keep polling
    } catch (err) {
      // Transient network errors don't kick us out — just log and wait
      // for the next tick. The backend's GET will retry against the provider.
      console.warn('Gateway pollCharge tick failed (will retry)', err);
    } finally {
      pollInFlightRef.current = false;
    }
  }

  async function handleCancel(): Promise<void> {
    const id = chargeIdRef.current;
    if (id && phase === 'waiting') {
      try {
        await gatewayPaymentAPI.cancelCharge(id);
      } catch (err) {
        console.warn('Gateway cancelCharge failed', err);
      }
    }
    go('cancelled');
    onCancel();
  }

  function handleRefresh(): void {
    void pollOnce();
  }

  function submitPhone(): void {
    if (phoneInput.replace(/\D/g, '').length < 7) {
      setErrorMessage(t({ en: 'Enter the customer’s mobile number.', ar: 'أدخل رقم جوال العميل.' }));
      return;
    }
    setErrorMessage(null);
    go('creating');
  }

  return (
    <div
      className="modal-overlay show"
      onClick={(e) => {
        if (e.target === e.currentTarget && phase !== 'waiting') handleCancel();
      }}
    >
      <div className="modal" style={{ width: 480, maxWidth: '92vw' }}>
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--accent-light, #dbeafe)', color: 'var(--accent, #2563eb)' }}
          >
            <CardIcon />
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {t({ en: `${gatewayName} payment`, ar: `دفع ${gatewayName}` })}
            </div>
            <div className="modal-header-name-ar">{fmt(total)}</div>
          </div>
          <button
            className="modal-close"
            onClick={handleCancel}
            disabled={phase === 'succeeded'}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
            style={{ marginLeft: 'auto' }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {phase === 'phone' && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                {t({
                  en: `${gatewayName} sends the customer a confirmation code by SMS. Enter their mobile number to continue.`,
                  ar: `يرسل ${gatewayName} رمز تأكيد للعميل عبر SMS. أدخل رقم جواله للمتابعة.`,
                })}
              </div>
              <input
                type="tel"
                inputMode="tel"
                autoFocus
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitPhone();
                }}
                placeholder={t({ en: 'Mobile number', ar: 'رقم الجوال' })}
                style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 16 }}
              />
              {errorMessage && (
                <div style={{ fontSize: 13, color: 'var(--danger, #dc2626)' }}>{errorMessage}</div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={onCancel} className="btn-ghost" style={ghostBtn}>
                  {t({ en: 'Back', ar: 'رجوع' })}
                </button>
                <button type="button" onClick={submitPhone} style={primaryBtn}>
                  {t({ en: 'Continue', ar: 'متابعة' })}
                </button>
              </div>
            </div>
          )}

          {phase === 'creating' && (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                {t({ en: `Opening ${gatewayName} payment…`, ar: `جارٍ فتح عملية دفع ${gatewayName}…` })}
              </div>
            </div>
          )}

          {phase === 'waiting' && terminalMode === 'qr' && (
            <>
              <div style={{ fontSize: 14, color: 'var(--text-muted)', textAlign: 'center' }}>
                {t({
                  en: 'Ask the customer to scan with their phone camera or banking app.',
                  ar: 'اطلب من العميل المسح باستخدام كاميرا الهاتف أو تطبيق البنك.',
                })}
              </div>
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={t({ en: `${gatewayName} payment QR`, ar: `رمز QR لدفع ${gatewayName}` })}
                  style={{ width: 320, height: 320, borderRadius: 8, border: '1px solid var(--border)' }}
                />
              ) : (
                <div style={{ width: 320, height: 320, display: 'grid', placeItems: 'center', color: 'var(--text-muted)' }}>
                  {t({ en: 'Generating QR…', ar: 'جارٍ إنشاء رمز QR…' })}
                </div>
              )}
              {redirectUrl && (
                <a
                  href={redirectUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12, color: 'var(--accent, #2563eb)', wordBreak: 'break-all', textAlign: 'center' }}
                >
                  {t({ en: 'Open link manually', ar: 'افتح الرابط يدويًا' })}
                </a>
              )}
              <PollingFooter chargeId={chargeId} pollCount={pollCount} onRefresh={handleRefresh} />
            </>
          )}

          {phase === 'waiting' && terminalMode === 'iframe' && redirectUrl && (
            <>
              <iframe
                src={redirectUrl}
                title={t({ en: `${gatewayName} payment`, ar: `دفع ${gatewayName}` })}
                style={{ width: '100%', height: 560, border: '1px solid var(--border)', borderRadius: 8 }}
                allow="payment *"
              />
              <PollingFooter chargeId={chargeId} pollCount={pollCount} onRefresh={handleRefresh} />
            </>
          )}

          {phase === 'succeeded' && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--success, #16a34a)' }}>
              <div style={{ fontSize: 32, marginBottom: 6 }}>✓</div>
              <div style={{ fontWeight: 600 }}>{t({ en: 'Payment captured', ar: 'تم تحصيل الدفعة' })}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                {t({ en: 'Receipt', ar: 'إيصال' })} #{orderResultRef.current?.entry_no} · {t({ en: 'printing…', ar: 'جارٍ الطباعة…' })}
              </div>
            </div>
          )}

          {phase === 'failed' && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--danger, #dc2626)' }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>!</div>
              <div style={{ fontWeight: 600 }}>
                {errorMessage ?? t({ en: `${gatewayName} charge failed`, ar: `فشلت عملية دفع ${gatewayName}` })}
              </div>
              <button type="button" onClick={onCancel} style={{ ...primaryBtn, marginTop: 16 }}>
                {t({ en: 'Back to payment methods', ar: 'العودة إلى طرق الدفع' })}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PollingFooter({
  chargeId,
  pollCount,
  onRefresh,
}: {
  chargeId: string | null;
  pollCount: number;
  onRefresh: () => void;
}): JSX.Element {
  const { t } = useLang();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
        {chargeId ? `${chargeId.slice(0, 12)}…` : ''} · {t({ en: 'poll', ar: 'استعلام' })} {pollCount}
      </div>
      <button type="button" onClick={onRefresh} style={ghostBtn}>
        {t({ en: 'Refresh now', ar: 'تحديث الآن' })}
      </button>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 18px',
  background: 'var(--accent, #2563eb)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 600,
};
const ghostBtn: React.CSSProperties = {
  padding: '6px 12px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
};

function CardIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h2" />
    </svg>
  );
}
